import { useFeatureGate } from '@statsig/expo-bindings';
import { Redirect } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    FeatureCard,
    PaycheckSplitFeatureCardGraphic,
} from '../../../common/components/FeatureCard';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import useShouldSeeIncomeSplit from '../../hooks/income-split/useShouldSeeIncomeSplit';
import useDeletePaycheckConfigs from '../../hooks/paycheck-routing/useDeletePaycheckConfigs';
import {
    detectedPaychecksAtom,
    existingPaycheckRoutingConfigsAtom,
} from '../../state/PaycheckRoutingAtoms';
import type { PaycheckRoutingIncomeDetail } from '../../types/PaycheckRouting';

interface DeleteButtonProps {
    onDelete: () => void;
}

const DeleteButton = (props: DeleteButtonProps) => {
    const { mutate: deleteConfigs, isPending } = useDeletePaycheckConfigs();
    const toast = useToastController();
    return (
        <XStack marginVertical={8}>
            <Button
                size={ButtonSize.SMALL}
                text="Clear routing rules"
                variant={ButtonVariant.SECONDARY}
                tone="danger"
                disabled={isPending}
                onPress={() => {
                    deleteConfigs(
                        {},
                        {
                            onSuccess: () => {
                                props.onDelete();
                                toast.show('Cleared paycheck routing rules', {
                                    duration: 2000,
                                });
                            },
                        },
                    );
                }}
            />
        </XStack>
    );
};

const getSetupWidget = (paychecks: PaycheckRoutingIncomeDetail[]) => {
    const setExistingPaycheckRoutingConfigs = useSetAtom(
        existingPaycheckRoutingConfigsAtom,
    );

    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();

    const { value: enablePaycheckRoutingClearButton } = useFeatureGate(
        FeatureFlags.ENABLE_PAYCHECK_ROUTING_CLEAR_BUTTON,
    );

    if (shouldSeeIncomeSplit) {
        return <Redirect href="/home/banking" />;
    }

    if (paychecks.length) {
        return (
            <YStack>
                {enablePaycheckRoutingClearButton && (
                    <DeleteButton
                        onDelete={() => {
                            setExistingPaycheckRoutingConfigs([]);
                        }}
                    />
                )}
            </YStack>
        );
    } else {
        return (
            <YStack gap={8}>
                <FeatureCard
                    name="Auto-split your paycheck"
                    descriptionAsList={[
                        'Open at least 2 Fruitful Cash Accounts',
                        'Set up a direct deposit',
                        'Set up rules based on your priorities',
                    ]}
                    href="/banking/paycheck-routing/info"
                    alt
                    graphic={<PaycheckSplitFeatureCardGraphic />}
                />
                {enablePaycheckRoutingClearButton && (
                    <DeleteButton
                        onDelete={() => {
                            setExistingPaycheckRoutingConfigs([]);
                        }}
                    />
                )}
            </YStack>
        );
    }
};

const PaycheckRoutingSetupWidget = () => {
    const paychecks: PaycheckRoutingIncomeDetail[] = useAtomValue(
        detectedPaychecksAtom,
    );
    return getSetupWidget(paychecks);
};

export default PaycheckRoutingSetupWidget;
